import { Box, Typography } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import TextField from 'components/text-field';

import Autocomplete from 'components/autocomplete';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import titleCase from 'utils/title-case';
import { getCalibrations } from 'views/bluviu-nutrition/features/calibrations/api/calibration';
import { CalibrationState } from 'views/bluviu-nutrition/features/calibrations/types/calibration-state.enum';
import { getGenerations } from 'views/bluviu-nutrition/features/scenarios-v2/api/generations-get';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioGeneration } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-generation.type';
import { UpdateScenarioOverviewInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-overview-input.type';

const OverviewSection: React.FC = () => {
  const intl = useIntl();
  const params = useParams<R2ScenarioParams>();
  const { control } = useFormContext<UpdateScenarioOverviewInput>();

  const { data: generations, isLoading: generationsIsLoading } = useGet(['getGenerations'], () => getGenerations());

  const { data: calibrations, isLoading: isCalibrationsLoading } = useGet(
    ['getCalibrations', params.globalSiteId],
    ({ queryKey }) => getCalibrations(String(queryKey[1])),
    {
      select: (data) =>
        _.chain(data)
          .filter((calibration) => calibration.state === CalibrationState.COMPLETED || calibration.state === CalibrationState.MODIFIED)
          .sortBy('name')
          .value(),
    }
  );

  return (
    <Box display={'flex'} flexDirection={'column'} gap={3}>
      <TextField
        dark
        fullWidth
        label={'Scenario name'}
        controller={{
          control,
          name: 'name',
          rules: {
            required: intl.formatMessage({ id: 'form.rules.required' }),
          },
        }}
      />
      <Box display={'grid'} gap={3} gridTemplateColumns={'repeat(3, 1fr)'}>
        <Autocomplete
          dark
          fullWidth
          label={'Generation'}
          disabled={generationsIsLoading}
          loading={generationsIsLoading}
          disableCloseOnSelect={false}
          options={generations ?? []}
          getOptionLabel={(option) => (option as ScenarioGeneration).displayName}
          controller={{
            control,
            name: 'generation',
            rules: {
              required: true,
            },
          }}
        />
        <Select label={'Model'} value={ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI} fullWidth disabled>
          <RadioSelectItem value={ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI}>
            {titleCase(ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI)}
          </RadioSelectItem>
        </Select>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            dark
            fullWidth
            label={'Calibration'}
            disabled={isCalibrationsLoading}
            disableCloseOnSelect={false}
            options={calibrations?.map((calibration) => ({ id: calibration.id, name: calibration.name })) ?? []}
            getOptionLabel={(option) => (option as { id: string; name: string }).name}
            controller={{
              control,
              name: 'calibration',
              rules: {
                required: true,
                validate: (value) => !!value?.id || intl.formatMessage({ id: 'form.rules.required' }),
              },
            }}
            helperText={
              <Typography color="text.secondary" fontSize={14}>
                Calibrate your scenario to previous data from your site.
              </Typography>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OverviewSection;
